import React, { useState } from "react";
import { Card, Row, Col, Affix, Alert } from "antd";
import LoadingIndicator from "../../components/shared/LoadingIndicator/LoadingIndicator";
import RefreshVisibilityAnalysisButton from "./RefreshVisibilityAnalysisButton";
import useDomainVisibilityAnalysis from "./hooks/useDomainVisibilityAnalysis";
import SearchPromptsList from "./SearchPromptsList";
import TopWebsites from "./TopWebsites";
import VisibilityAnalysisScores from "./VisibilityAnalysisScores";
import "./VisibilityAnalysis.less";


const { Meta } = Card;
type Props = {
    domain: string;
}

export default function VisibilityAnalysis({ domain }: Props) {
    const [, setIsRefreshingAnalysis] = useState(false);
    const { data: domainVisibilityAnalysis, isLoading, isInProgress } = useDomainVisibilityAnalysis(domain);

    const topWebsites = domainVisibilityAnalysis ? domainVisibilityAnalysis.top_websites : [];
    const firstTopWebsite = topWebsites.length > 0 ? topWebsites[0] : null;
    const lastTopWebsite = topWebsites.length > 0 ? topWebsites[topWebsites.length - 1] : null;

    return (
        <Card
            className="llm-visibility-analysis"
            title={
                <Meta title={"LLM Visibility Analysis"} description={"Prompts that users might ask LLMs to discover websites like yours"} />
            }
            extra={
                !isLoading ? <RefreshVisibilityAnalysisButton domain={domain} inProgress={isInProgress} setIsRefreshingAnalysis={setIsRefreshingAnalysis} /> : null
            }
        >
            {isLoading && <LoadingIndicator />}
            {!isLoading && isInProgress && (
                <>
                    <Alert
                        className="analysis-in-progress-alert"
                        type="info"
                        showIcon
                        message={
                            <div className="analysis-progress">
                                <div className="title">LLM Visibility Analysis in progress</div>
                                <div className="loader"></div>
                            </div>
                        }
                        description={
                            <>
                                <span className="alert-message">
                                    We are performing an LLM Visibility Analysis for your domain. This may take a couple of minutes, depending on your domain's content complexity.&nbsp;
                                </span>
                                <span className="alert-message">
                                    Feel free to leave this page - your analysis will continue running in the background.
                                </span>
                            </>
                        }
                    />
                    <LoadingIndicator />
                </>
            )}

            {!isLoading && !isInProgress && domainVisibilityAnalysis && (
                <Row gutter={24} style={{ paddingTop: "8px" }}>
                    <Col span={18} className="search-prompts-column">
                        <Alert
                            message={
                                <span>
                                    The <strong>LLM Visibility Analysis</strong> measures how often a website is recommended in AI-generated answers to the most common questions people are likely to ask LLMs about
                                    the website's niche. Instead of looking at traditional search engine rankings, this analysis focuses on responses produced by LLMs to reveal whether the website is mentioned,
                                    how frequently it appears, and how it compares with competing domains. This helps understand the website's recognition in AI-driven search and highlight its relative standing
                                    in the competitive landscape of its niche.
                                </span>
                            }
                            type="info"
                            showIcon
                        />
                        <SearchPromptsList
                            domain={domain}
                            niche={domainVisibilityAnalysis.industry_niche}
                            searchPrompts={domainVisibilityAnalysis.search_prompts}
                        />
                    </Col>

                    <Col span={6} className="top-websites-column">
                        <VisibilityAnalysisScores
                            targetDomain={domainVisibilityAnalysis.target_domain}
                            firstMentionedDomain={firstTopWebsite}
                            lastMentionedDomain={lastTopWebsite}
                        />

                        <Affix offsetTop={70} className="sticky">
                            <TopWebsites
                                domain={domain}
                                topWebsitePerformances={domainVisibilityAnalysis.top_websites}
                            />
                        </Affix>
                    </Col>
                </Row>
            )}
        </Card>
    );
}